import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import Button from "../../components/ui/button/Button";
import { TableSkeleton } from "../../components/ui/skeleton";
import { Modal } from "../../components/ui/modal";
import { useModal } from "../../hooks/useModal";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import {
  fetchTenantUsers,
  createTenantUser,
  deactivateTenantUser,
  setTenantAdmin,
  TeamMember,
  TenantUsersResponse,
  CreateUserPayload,
} from "../../lib/api/tenant";
import { fetchUserProfile, UserProfile } from "../../lib/api/user";

interface NewAccountBanner {
  email: string
  tempPassword: string
}

export default function Members() {
  const [data, setData] = useState<TenantUsersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const createModal = useModal();
  const [formData, setFormData] = useState<CreateUserPayload>({ email: "", first_name: "", last_name: "", title: "" });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [newAccountBanner, setNewAccountBanner] = useState<NewAccountBanner | null>(null);
  const [copied, setCopied] = useState(false);

  const [confirmDeactivateId, setConfirmDeactivateId] = useState<number | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [togglingAdminId, setTogglingAdminId] = useState<number | null>(null);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchTenantUsers();
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load team members.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    fetchUserProfile().then(setCurrentUser).catch(() => {});
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    try {
      setSubmitting(true);
      const payload: CreateUserPayload = {
        email: formData.email,
        ...(formData.first_name ? { first_name: formData.first_name } : {}),
        ...(formData.last_name ? { last_name: formData.last_name } : {}),
        ...(formData.title ? { title: formData.title } : {}),
      };
      const result = await createTenantUser(payload);
      createModal.closeModal();
      setFormData({ email: "", first_name: "", last_name: "", title: "" });
      setNewAccountBanner({ email: result.email, tempPassword: result.temp_password });
      setCopied(false);
      loadUsers();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to create account.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      setDeactivating(true);
      await deactivateTenantUser(id);
      setConfirmDeactivateId(null);
      loadUsers();
    } catch (err) {
      console.error("[Members] Deactivate error:", err);
    } finally {
      setDeactivating(false);
    }
  };

  const handleCopyPassword = (password: string) => {
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleToggleAdmin = async (member: TeamMember) => {
    setTogglingAdminId(member.id);
    try {
      await setTenantAdmin(member.id, !member.is_tenant_admin);
      loadUsers();
    } catch (err) {
      console.error("[Members] Toggle admin error:", err);
    } finally {
      setTogglingAdminId(null);
    }
  };

  const seats = data?.seats;
  const atCapacity = seats?.available === 0;

  return (
    <>
      <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
        {/* Seat usage */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-white/[0.05]">
          {seats && (
            seats.limit === null ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Unlimited seats</p>
            ) : (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-300 font-medium">
                    {seats.used} / {seats.limit} seats used
                  </span>
                  {seats.available !== null && (
                    <span className="text-gray-400 dark:text-gray-500 text-xs">
                      {seats.available} remaining
                    </span>
                  )}
                </div>
                <div className="w-full h-2 bg-gray-100 dark:bg-white/[0.06] rounded-full overflow-hidden">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      atCapacity
                        ? "bg-error-500"
                        : seats.used / seats.limit > 0.8
                        ? "bg-warning-500"
                        : "bg-brand-500"
                    }`}
                    style={{ width: `${Math.min(100, (seats.used / seats.limit) * 100)}%` }}
                  />
                </div>
              </div>
            )
          )}
        </div>

        {/* Header */}
        <div className="px-6 py-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Team Members
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {data ? `Total: ${data.users.length} member${data.users.length !== 1 ? "s" : ""}` : ""}
              </p>
            </div>
            <div title={atCapacity ? "Seat limit reached" : undefined}>
              <Button
                size="sm"
                onClick={createModal.openModal}
                disabled={atCapacity}
              >
                Create Account
              </Button>
            </div>
          </div>
        </div>

        {/* New account banner */}
        {newAccountBanner && (
          <div className="mx-6 mb-4 rounded-lg bg-success-50 dark:bg-success-500/15 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-success-700 dark:text-success-400 mb-1">
                  Account created for {newAccountBanner.email}
                </p>
                <p className="text-xs text-success-600 dark:text-success-500 mb-2">
                  A welcome email has been sent with their temporary password. Copy it below as a backup — it will not be shown again.
                </p>
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono bg-white dark:bg-white/[0.08] border border-success-200 dark:border-success-500/30 rounded px-2 py-1 text-gray-800 dark:text-white">
                    {newAccountBanner.tempPassword}
                  </code>
                  <button
                    onClick={() => handleCopyPassword(newAccountBanner.tempPassword)}
                    className="text-xs font-medium text-success-700 dark:text-success-400 hover:underline whitespace-nowrap"
                  >
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
              <button
                onClick={() => setNewAccountBanner(null)}
                className="text-success-500 hover:text-success-700 dark:text-success-400 dark:hover:text-success-300 flex-shrink-0 mt-0.5"
                aria-label="Dismiss"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M4.293 4.293a1 1 0 011.414 0L8 6.586l2.293-2.293a1 1 0 111.414 1.414L9.414 8l2.293 2.293a1 1 0 01-1.414 1.414L8 9.414l-2.293 2.293a1 1 0 01-1.414-1.414L6.586 8 4.293 5.707a1 1 0 010-1.414z" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="px-6 pb-4">
            <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
              <p className="text-sm font-medium text-error-700 dark:text-error-400">{error}</p>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="overflow-hidden">
            <div className="max-w-full overflow-x-auto">
              <TableSkeleton rows={4} columns={5} />
            </div>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && data && data.users.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <p className="text-gray-500 dark:text-gray-400">No team members found.</p>
          </div>
        )}

        {/* Table */}
        {!loading && !error && data && data.users.length > 0 && (
          <div className="overflow-hidden">
            <div className="max-w-full overflow-x-auto">
              <Table>
                <TableHeader className="border-gray-100 border-y dark:border-white/[0.05]">
                  <TableRow>
                    <TableCell isHeader className="py-3 px-6 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                      Name
                    </TableCell>
                    <TableCell isHeader className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                      Email
                    </TableCell>
                    <TableCell isHeader className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                      Title
                    </TableCell>
                    <TableCell isHeader className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                      Status
                    </TableCell>
                    <TableCell isHeader className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                      Admin
                    </TableCell>
                    <TableCell isHeader className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                      <span className="sr-only">Actions</span>
                    </TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                  {data.users.map((member: TeamMember) => (
                    <TableRow key={member.id}>
                      <TableCell className="px-6 py-4">
                        <span className="font-medium text-gray-800 text-theme-sm dark:text-gray-300">
                          {[member.first_name, member.last_name].filter(Boolean).join(" ") || "—"}
                        </span>
                      </TableCell>
                      <TableCell className="px-6 py-4 text-gray-700 text-theme-sm dark:text-gray-400">
                        {member.email}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-gray-700 text-theme-sm dark:text-gray-400">
                        {member.title || "—"}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            member.is_active
                              ? "bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-300"
                              : "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300"
                          }`}
                        >
                          {member.is_active ? "Active" : "Inactive"}
                        </span>
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {currentUser?.is_tenant_admin ? (
                          <button
                            onClick={() => handleToggleAdmin(member)}
                            disabled={togglingAdminId === member.id || member.id === currentUser?.id}
                            title={member.id === currentUser?.id ? "Cannot change your own admin status" : undefined}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${
                              member.is_tenant_admin ? "bg-brand-500" : "bg-gray-200 dark:bg-gray-700"
                            }`}
                          >
                            <span
                              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                member.is_tenant_admin ? "translate-x-4" : "translate-x-1"
                              }`}
                            />
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {member.is_tenant_admin ? "Yes" : "No"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {member.is_active && (
                          confirmDeactivateId === member.id ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">Deactivate?</span>
                              <button
                                onClick={() => handleDeactivate(member.id)}
                                disabled={deactivating}
                                className="text-xs font-medium text-error-600 dark:text-error-400 hover:underline disabled:opacity-50"
                              >
                                {deactivating ? "..." : "Yes"}
                              </button>
                              <button
                                onClick={() => setConfirmDeactivateId(null)}
                                className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:underline"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDeactivateId(member.id)}
                              className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-error-600 dark:hover:text-error-400"
                            >
                              Deactivate
                            </button>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* Create Account Modal */}
      <Modal
        isOpen={createModal.isOpen}
        onClose={createModal.closeModal}
        className="max-w-[600px] p-5 lg:p-10"
      >
        <form onSubmit={handleCreate}>
          <h4 className="mb-6 text-lg font-medium text-gray-800 dark:text-white/90">
            Create Account
          </h4>

          {formError && (
            <div className="mb-4 rounded-lg bg-error-50 p-3 dark:bg-error-500/15">
              <p className="text-sm text-error-700 dark:text-error-400">{formError}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <Input
                  type="text"
                  placeholder="First name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  type="text"
                  placeholder="Last name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Title</Label>
              <Input
                type="text"
                placeholder="e.g. Regulatory Engineer"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center justify-end w-full gap-3 mt-6">
            <Button
              size="sm"
              variant="outline"
              onClick={createModal.closeModal}
              type="button"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button size="sm" type="submit" disabled={submitting}>
              {submitting ? "Creating..." : "Create Account"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
